import { z } from 'zod';

// Analytics (/api/analytics). The knowledge agent's tools read these routes in-process with the
// same query strings. The dimension lists are the ones the routes and the tools use.

const Digits = z.string().regex(/^\d+$/, 'must be a whole number');
/** Blank means no bound. */
const DateText = z.string().refine((s) => s === '' || !Number.isNaN(Date.parse(s)), 'is not a date');

export const DemographicDimension = z.enum([
  'organization',
  'sector',
  'geographicRegion',
  'preferredLanguage',
  'careerSeniority',
  'discipline',
  'province',
  'country',
  'healthProfessional',
  'genderIdentity',
  'preferredPronouns',
]);
export type DemographicDimension = z.infer<typeof DemographicDimension>;

export const ReachBooleanDimension = z.enum([
  'isRacialized',
  'isIndigenous',
  'hasDisability',
  'is2SLGBQA',
  'hasSocioEconomicDisadvantages',
]);
export type ReachBooleanDimension = z.infer<typeof ReachBooleanDimension>;

export const ReachCategoryDimension = z.enum([
  'genderIdentity',
  'geographicRegion',
  'careerSeniority',
  'discipline',
  'sector',
]);
export type ReachCategoryDimension = z.infer<typeof ReachCategoryDimension>;

export const ReachDimension = z.enum([...ReachBooleanDimension.options, ...ReachCategoryDimension.options]);
export type ReachDimension = z.infer<typeof ReachDimension>;

export const MemberTimelineParams = z.strictObject({ userId: z.guid() });
export type MemberTimelineParams = z.input<typeof MemberTimelineParams>;

/** GET /api/analytics/members/:userId/timeline; limit defaults to 50, at most 200. */
export const MemberTimelineQuery = z.strictObject({
  since: DateText.optional(),
  until: DateText.optional(),
  limit: Digits.optional(),
});
export type MemberTimelineQuery = z.input<typeof MemberTimelineQuery>;

/** GET /api/analytics/mentorship/reach; dimension defaults to isRacialized. */
export const MentorshipReachQuery = z.strictObject({
  dimension: z.enum([...ReachDimension.options, ''], { error: 'Unknown dimension' }).optional(),
  seasonId: Digits.optional(),
});
export type MentorshipReachQuery = z.input<typeof MentorshipReachQuery>;

/** GET /api/analytics/mentorship/summary */
export const MentorshipSummaryQuery = z.strictObject({ seasonId: Digits.optional() });
export type MentorshipSummaryQuery = z.input<typeof MentorshipSummaryQuery>;

/** GET /api/analytics/attendance/summary; groupBy defaults to month. An unknown source counts nothing. */
export const AttendanceSummaryQuery = z.strictObject({
  userId: z.union([z.guid(), z.literal('')]).optional(),
  since: DateText.optional(),
  until: DateText.optional(),
  source: z.string().optional(),
  groupBy: z.enum(['month', 'user', '']).optional(),
});
export type AttendanceSummaryQuery = z.input<typeof AttendanceSummaryQuery>;

/** GET /api/analytics/demographics; dimension defaults to sector, limit to 20 (at most 100). */
export const DemographicsQuery = z.strictObject({
  dimension: z.union([DemographicDimension, z.literal('')]).optional(),
  limit: Digits.optional(),
  since: DateText.optional(),
  until: DateText.optional(),
});
export type DemographicsQuery = z.input<typeof DemographicsQuery>;

/** GET /api/analytics/equity-summary */
export const EquitySummaryQuery = z.strictObject({ since: DateText.optional(), until: DateText.optional() });
export type EquitySummaryQuery = z.input<typeof EquitySummaryQuery>;

/** GET /api/analytics/attention/:kind; an unknown kind is a 404. */
export const AttentionParams = z.strictObject({ kind: z.string() });
export type AttentionParams = z.input<typeof AttentionParams>;
